import React, { useRef, forwardRef, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Select, Form } from 'antd';

import { renderNoDataOrSpinner } from '../../../../../../shared/utils';

import * as actions from '../../../../store/actions';

let contactsFieldTimeout;

const PhoneBookForm = forwardRef(({ insuredId, policyId }, ref) => {
  const formRef = useRef();

  const [selectedContactType, setSelectedContactType] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);

  const [formSearchFields, setFormSearchFields] = useState({
    contactsSearchValue: '',
  });

  const dispatch = useDispatch();

  const phonebookContacts = useSelector((state) => state.phoneBook.phonebookContacts);
  const phonebookContactsLoading = useSelector((state) => state.phoneBook.phonebookContactsLoading);

  // The component instance will be extended
  // with whatever you return from the callback passed
  // as the second argument
  useImperativeHandle(ref, () => ({
    getSelectedContactInfo() {
      return {
        emails: formRef.current.getFieldValue('emails'),
        fax: formRef.current.getFieldValue('fax'),
      };
    },
  }));

  const handleContactTypeChange = (selectedValue) => {
    setSelectedContactType(selectedValue);

    formRef.current.setFieldsValue({
      contact: null,
    });

    dispatch(actions.getContactsByType(selectedValue, insuredId, policyId));
  };

  const handleSearchContacts = (searchString) => {
    if (contactsFieldTimeout) {
      clearTimeout(contactsFieldTimeout);
      contactsFieldTimeout = null;
    }

    const updatedFormSearchFields = { ...formSearchFields };
    updatedFormSearchFields.contactsSearchValue = searchString;

    setFormSearchFields(updatedFormSearchFields);

    contactsFieldTimeout = setTimeout(() => dispatch(actions.getContactsByType(selectedContactType, insuredId, policyId, searchString)), 500);
  };

  const handleSelectContact = (key) => {
    if (phonebookContacts && phonebookContacts.length) {
      const selected = phonebookContacts.find((c) => c.id === key);

      setSelectedContact(selected);
    }
  };

  return (
    <div id="phone-book-form">
      <Form ref={formRef} labelCol={{ span: 4 }} wrapperCol={{ span: 20 }} preserve={false} layout="horizontal" size="small" labelAlign="left">
        <Form.Item label="Contact type:">
          <Select
            showArrow
            placeholder="Select"
            getPopupContainer={() => document.getElementById('phone-book-form')}
            onChange={(value) => handleContactTypeChange(value)}
          >
            <Select.Option key="0" value={0}>
              Carrier
            </Select.Option>
            <Select.Option key="1" value={1}>
              MGA
            </Select.Option>
            <Select.Option key="2" value={2}>
              Finance company
            </Select.Option>
            <Select.Option key="3" value={3}>
              Holders
            </Select.Option>

            {insuredId ? (
              <Select.Option key="4" value={4}>
                Insured contact
              </Select.Option>
            ) : null}
            {policyId ? (
              <Select.Option key="5" value={5}>
                Policy contact
              </Select.Option>
            ) : null}
          </Select>
        </Form.Item>

        <Form.Item name="contact" label="Contact:">
          <Select
            showArrow
            allowClear
            showSearch
            filterOption={false}
            searchValue={formSearchFields.contactsSearchValue}
            onSearch={handleSearchContacts}
            notFoundContent={renderNoDataOrSpinner(phonebookContactsLoading)}
            disabled={selectedContactType === null}
            placeholder={`Select or type to search`}
            getPopupContainer={() => document.getElementById('phone-book-form')}
            loading={phonebookContactsLoading}
            onSelect={handleSelectContact}
          >
            {phonebookContacts && phonebookContacts.length
              ? phonebookContacts.map((contact) => (
                  <Select.Option key={contact.id} value={contact.id}>
                    {contact.name}
                  </Select.Option>
                ))
              : null}
          </Select>
        </Form.Item>

        <Form.Item name="emails" label="Email:">
          <Select
            showArrow
            allowClear
            mode="multiple"
            disabled={selectedContactType === null}
            placeholder={`Select ${selectedContactType === null ? 'contact type first' : ''}`}
            getPopupContainer={() => document.getElementById('phone-book-form')}
          >
            {selectedContact ? (
              <React.Fragment key={selectedContact.id}>
                {selectedContact.name && selectedContact.email ? (
                  <Select.Option key={selectedContact.id + 'main'} value={selectedContact.email}>
                    {selectedContact.name} ({selectedContact.email}) - Main
                  </Select.Option>
                ) : null}

                {selectedContact.name && selectedContact.email2 ? (
                  <Select.Option key={selectedContact.id + 'main2'} value={selectedContact.email2}>
                    {selectedContact.name} ({selectedContact.email2}) - Main 2
                  </Select.Option>
                ) : null}

                {selectedContact.name && selectedContact.email3 ? (
                  <Select.Option key={selectedContact.id + 'main3'} value={selectedContact.email3}>
                    {selectedContact.name} ({selectedContact.email3}) - Main 3
                  </Select.Option>
                ) : null}

                {selectedContact.contacts && selectedContact.contacts.length
                  ? selectedContact.contacts
                      .filter((f) => f.email !== '')
                      .map((childCont) => (
                        <Select.Option key={childCont.id + 'sub'} value={childCont.email}>
                          {childCont.name} ({childCont.email}) - {childCont.typeString}
                        </Select.Option>
                      ))
                  : null}
              </React.Fragment>
            ) : null}
          </Select>
        </Form.Item>

        <Form.Item name="fax" label="Fax:">
          <Select
            showArrow
            allowClear
            disabled={selectedContactType === null}
            placeholder={`Select ${selectedContactType === null ? 'contact type first' : ''}`}
            getPopupContainer={() => document.getElementById('phone-book-form')}
          >
            {selectedContact ? (
              <React.Fragment key={selectedContact.id}>
                {selectedContact.name && selectedContact.fax ? (
                  <Select.Option key={selectedContact.id} value={selectedContact.fax}>
                    {selectedContact.name} ({selectedContact.fax}) - Main
                  </Select.Option>
                ) : null}

                {selectedContact.contacts && selectedContact.contacts.length
                  ? selectedContact.contacts
                      .filter((f) => f.fax !== '')
                      .map((childCont) => (
                        <Select.Option key={childCont.id + 'sub'} value={childCont.fax}>
                          {childCont.name} ({childCont.fax}) - {childCont.typeString}
                        </Select.Option>
                      ))
                  : null}
              </React.Fragment>
            ) : null}
          </Select>
        </Form.Item>
      </Form>
    </div>
  );
});

export default PhoneBookForm;
